import { getVideoThumbnailUrl } from '../utils/mediaThumbnail';

// Ratios offered per breakpoint (values are stored as "w/h"; "16/9" is the storefront default).
export const DESKTOP_RATIOS = ['21/9', '16/9', '3/2', '4/3', '1/1'];
export const MOBILE_RATIOS = ['16/9', '16/10', '4/3', '1/1', '4/5', '9/16'];
const DEFAULT_RATIO = '16/9';

// Reference screens the preview draws. `scale` only shrinks the drawing to fit the admin card.
const DEVICES = {
  desktop: { label: 'Desktop', name: '1440 × 900', width: 1440, fold: 900, scale: 0.3 },
  mobile: { label: 'Mobile', name: '390 × 844', width: 390, fold: 844, scale: 0.5 },
};
const PAGE_MARGIN = 32; // storefront section has my-8

const parseRatio = (ratio) => {
  const [w, h] = String(ratio).split('/').map(Number);
  return w > 0 && h > 0 ? { w, h } : parseRatio(DEFAULT_RATIO);
};

const RatioGlyph = ({ ratio }) => {
  const { w, h } = parseRatio(ratio);
  const box = 22;
  return (
    <span
      className="inline-block border border-current rounded-[2px]"
      style={{
        width: w >= h ? box : (box * w) / h,
        height: w >= h ? (box * h) / w : box,
      }}
    />
  );
};

/** Row of ratio buttons. `value` may be '' / unknown — shown as the 16:9 default. */
export const RatioPicker = ({ label, hint, options, value, onChange }) => {
  const selected = options.includes(value) ? value : DEFAULT_RATIO;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <label className="block text-sm font-medium">{label}</label>
        <span className="text-xs text-muted-foreground/70">{hint}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map((ratio) => (
          <button
            key={ratio}
            type="button"
            onClick={() => onChange(ratio)}
            aria-pressed={selected === ratio}
            className={`flex flex-col items-center justify-center gap-1 w-14 h-14 rounded-lg border text-[11px] transition ${
              selected === ratio
                ? 'border-foreground bg-primary text-primary-foreground'
                : 'border-input text-muted-foreground hover:border-foreground hover:text-foreground'
            }`}
          >
            <RatioGlyph ratio={ratio} />
            {ratio.replace('/', ':')}
          </button>
        ))}
      </div>
    </div>
  );
};

const DeviceFrame = ({ device, ratio, mediaUrl, heading, buttonText }) => {
  const { label, name, width, fold, scale } = DEVICES[device];
  const { w, h } = parseRatio(ratio);
  const sectionHeight = Math.round((width * h) / w);
  const pageHeight = sectionHeight + PAGE_MARGIN * 2;
  const hasText = Boolean(heading || buttonText);
  const thumb = getVideoThumbnailUrl(mediaUrl, { width: 800 });
  const isDesktop = device === 'desktop';

  return (
    <div>
      <p className="text-xs font-medium mb-1">
        {label} <span className="text-muted-foreground/70 font-normal">({name})</span>
      </p>
      <div
        className="relative overflow-hidden rounded-lg border border-input bg-muted"
        style={{ width: width * scale, height: pageHeight * scale }}
      >
        <div
          className="relative"
          style={{ width, height: pageHeight, transform: `scale(${scale})`, transformOrigin: 'top left' }}
        >
          <div
            className="relative overflow-hidden bg-black"
            style={{ margin: `${PAGE_MARGIN}px 0`, height: sectionHeight }}
          >
            {thumb ? (
              <img src={thumb} alt="" className="w-full h-full object-cover" />
            ) : (
              <video
                src={`${mediaUrl}#t=0.1`}
                muted
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />
            )}
            {hasText && <div className="absolute inset-0 bg-black/30" />}
            {hasText && (
              <div className="absolute inset-0 flex items-center justify-center text-center text-white">
                <div>
                  {heading && (
                    <div className={`font-serif ${isDesktop ? 'text-6xl mb-4' : 'text-xl mb-2'}`}>{heading}</div>
                  )}
                  {buttonText && (
                    <div
                      className={`inline-block border border-white ${isDesktop ? 'px-8 py-3 mt-6 text-base' : 'px-5 py-2 mt-3 text-sm'}`}
                    >
                      {buttonText}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {fold < pageHeight && (
            // Sizes are divided by `scale` so the marker stays readable after the frame is shrunk.
            <div
              className="absolute left-0 right-0 border-dashed border-red-400"
              style={{ top: fold, borderTopWidth: 2 / scale }}
            >
              <span
                className="absolute right-2 text-red-500 bg-white/80 px-2 rounded-lg"
                style={{ fontSize: 11 / scale, top: 4 / scale }}
              >
                screen fold
              </span>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {ratio.replace('/', ':')} → {width} × {sectionHeight}px
      </p>
    </div>
  );
};

/** Scaled mock of the Our Story video section on a reference desktop and phone screen. */
export const OurStoryPreview = ({ mediaUrl, ratioDesktop, ratioMobile, heading, buttonText }) => (
  <div>
    <label className="block text-sm font-medium mb-2">Preview</label>
    <div className="flex flex-wrap items-start gap-6 p-4 bg-muted/50 border border-border rounded-lg">
      <DeviceFrame
        device="desktop"
        ratio={DESKTOP_RATIOS.includes(ratioDesktop) ? ratioDesktop : DEFAULT_RATIO}
        mediaUrl={mediaUrl}
        heading={heading}
        buttonText={buttonText}
      />
      <DeviceFrame
        device="mobile"
        ratio={MOBILE_RATIOS.includes(ratioMobile) ? ratioMobile : DEFAULT_RATIO}
        mediaUrl={mediaUrl}
        heading={heading}
        buttonText={buttonText}
      />
    </div>
    <p className="text-xs text-muted-foreground/70 mt-1">
      Video is centre-cropped to fit the ratio. Reflects unsaved changes; the real page also has the site header.
    </p>
  </div>
);
